import { COMPONENT_ACTIONS } from 'app/infrastructure/ui/builder/component-actions';
import { columnName, tableName } from 'app/area/case/case-data-model';
import { scheduleUpdate } from '../platform';
/**
 *
 * This is the file where you do all configuration to plug a Synergi Life UI Component into the UI engine
 *
 */

/**
 * Describes the defaults for a component when it's used in the scope of ui-engine
 * The most important is 'key' which is required for all components.
 *
 * @export
 * @interface ComponentConfigurations
 */
export interface ComponentConfigurations{
  [indexer: string] : {
    props: Object;
    /**
     * List the events this component supports among those listed in [[]]
     *
     * @type {string[]}
     */
    events: string[];
    /**
     * The identitifer in database persisted domain attributes for this component
     * e.g 'dateTime' will resolve all 'dateTime.xxx' keys among domain attributes and spread them onto
     * the DateTime component
     *
     * @type {string}
     */
    attributeKey: string;
  };
}

export enum IteratorType {
  DataRows = 1,
  CodeTable = 2
}

/***
 * Maps the numeric db representation of a control to a client side component type.
 *
 * Add your component plus it's database domain type id here.
 */
export enum ControlType {

  NotImplemented = -1,

  Schema = 5000,
  TextBox = 5001,
  TextArea = 5002,
  NumberBox = 5003,
  DateTime = 5004,

  SelectMultiple = 5005,
  SelectMultipleHierarchy = 5006,

  Button = 5007,
  CheckBox = 5008,
  Form = 5009,
  Hidden = 5011, // is this relevant?

  TileView = 5012,

  SelectSingleHierarchy = 5015,
  SelectSingle = 5016,
  SelectSingleShortList = 5017,
  EntityKeyCategorySelectSingleShortList = 5076,

  CommentTileView = 5018,
  CommentTile = 5019,

  SliderRating = 5020,
  EntityKeyCategorySliderRating = 5048,

  ButtonList = 5021,

  HeaderPanel = 5022,
  DisplayCodes = 5023,
  DisplayDate = 5024,
  DisplayNumber = 5025,
  DisplayText = 5026,

  ProcessPanel = 5027,
  GroupPanel = 5028,

  FlexPanel = 5029,
  FluidPanel = 5030,
  ExpandableText = 5031,
  ColumnsPanel = 5032,
  LinkHeading = 5033,
  SectionPanel = 5053,
  FlexRowPanel = 5054,

  DetailForm = 5034,

  DisplayChildValues = 5036,
  AssessmentFactItem = 5037,
  FactBox = 5035,

  ConnectedCasesTileView = 5038,

  FileUploader = 5040,

  TileTopZone = 5041,

  CodeTitleTile = 5043,

  LossPotentialEvaluation = 5042,
  LossPotentialConsequenceEvaluation = 5078,
  LossPotentialConsequenceEvaluationTileView = 5044,

  CodeSelectorTileView = 5045,
  CategoryTileView = 5046,

  LossPotentialEvaluationTileView = 5047,

  DisplayNumericHeader = 5049,

  Tile = 5050,

  DisplayCodesHierarchy = 5051,

  DisplayBoolean = 5052,

  ConsequenceTypeTileView = 5055,

  ConsequenceTileView = 5056,

  ConsequenceTypeTile = 5057,

  LongDescription = 5058,

  ConnectedCasesTile = 5059,

  CategoryTile = 5060,

  CauseCategoryTileView = 5061,

  SectionSummaryPanel = 5063,

  CheckListCategoryTileView = 5064,

  DisplayNumericSum = 5066,

  AttachLinkTileView = 5065,

  DisplaySafeAnchor = 5067,

  AttachLinkTile = 5069,

  CheckListCategoryTile = 5068,

  SafeAnchor = 5070,

  CheckListEvaluationPanel = 5071,

  SubstanceCategoryTileView = 5072,

  EnvironmentalValuesTileView = 5073,

  InteractionTileView = 5074,

  InteractionTile = 5075,

  CausesTileView = 5077,

  DisplayCodesUnitOfMeasure = 5079,

  DisplayNumberUnitOfMeasure = 5080,

  CompactCodeTitleTile = 5081,

  SummaryTileView = 5082,

  TitleWrapperPrimaryKey = 5083,

  LabelPanel = 5084,

  InteractionTileViewDrugReaction = 5085,

  ReferenceTileView = 5086,

  ReferenceDate = 5087,

  ReferenceTextBox = 5088,

  ReferenceNumberBox = 5089,

  ReferenceEntityKeyCategorySelectSingleShortList = 5090,

  ReferenceCheckBox = 5091,

  ReferenceSelectSingleHierarchy = 5092,

  Signature = 5093,

  SurveyLink = 5094,

  ReferenceDateTime = 5095,

  ReferenceSelectSingle = 5096,

  DisplayCodesReference = 5097,

  ReferenceTile = 5098,

  RiskMatrix = 5099,

  DisplayInsuranceBalance = 5102,

  IndicatorCodeSelectorTileView = 5103,

  AddDocumentItemLinkButton = 5104,

  ProcessingLogView = 5105,
  ProcessingLogDetails = 5106,
  ProcessingLogLink = 5107,

  MailLogLink = 5108,
  MailLogDetails = 5109,
  CaseLogLink = 5110,

  AddNewCaseButton = 5111,

  CompactTileHeader = 5112,
  ConnectedCasesFactbox = 5113,

  CheckForDuplicateRegistrationsButton = 5114,
  Link = 5115,
  DisplaySurveyLink = 5116,

  LinkSearchExternalProvider = 5117,

  CompactTile = 5118,

  ConnectToBowtieButton = 5119,

  BarrierInspectionTileView = 5220,

  DisplayNumberBarrierInspection = 5221,

  DisplayCodesStatus = 5222,

  SelectSingleSignature = 5223,

  DisplayEnvironmentalPreviousValues = 5224,

  ShareLinkButton = 5225,

  MultiSelectRecipients = 5226,

  BadgeListFileUploader = 5227,

  Thumbnail = 5228,

  DetailsPanelHeader = 5229,
  
  LinkButtonFileUploader = 5230,

  Date = 5231,

  FileUploaderList = 5232,

  ChangeLogLink = 5233,

  LossPotentialEvaluationFooter = 5234,

  LossPotentialConsequenceEvaluationFooter = 5235,

  LossPotentialConsequenceEvaluationTileRiskMatrixPanel = 5236,

  LossPotentialConsequenceEvaluationTileDetailPanel = 5237,

  ButtonGroupPanel = 5238,

  BowtieFactItem = 5239,

  CausesFactItem = 5240,
 
  AttachedFilesFactItem = 5241,  

  IntegrityStatusBarrierEvalFactItem = 5242,

  ChecklistFactItem = 5243,

  CommentsFactItem = 5244,

  ConnectedCasesFactItem = 5245,

  ConsequencesFactItem = 5246,

  IntegrityStatusFactItem = 5247,

  InvolvedDrugFactItem = 5248,

  InvolvedPersonFactItem = 5249,

  ResidualRiskFactItem = 5250,

  RiskFactItem = 5251,

  ActionFactItem = 5252,

  FactItemPanel = 5253,

  ConnectedCasesBowtieFactItem = 5254,

  ConnectedCasesRiskFactItem = 5255,

  ConnectedCasesResidualRiskFactItem = 5256,

  ConnectedCasesIntegrityStatusFactItem = 5257,

  ConnectedCasesIntegrityStatusBarrierEvalFactItem = 5258,

  ConnectedCasesActionFactItem = 5259,

  ImagesFactItem = 5260,

  DisplayLink = 5261,

  MainView = 5262,

  ReportCategoryFactItem = 5263,

  ConnectedCasesReportCategoryFactItem = 5264,

  GasMeasurementCodeSelectorTileView = 5265,

  GasTestFactItem = 5266,

  CompactTileLinkHeading = 5267
}

/**
 * This defines the default behaviour for a component, independent of render mode.
 *
 * Add your prefix for domain attributes (follow the existing pattern) and relevant 'onChange' handler.
 *
 * NOTE: onBlur and onFocus events gets applied to all components that edit so no need to set them explicitly.
 *
 */
export const componentDefaults: ComponentConfigurations = {

  [ControlType.NotImplemented]: {
    attributeKey: 'NA',
    props: {},
    events: []
  },

  [ControlType.MainView]: {
    attributeKey: 'NA',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.Schema]: {
    attributeKey: 'NA',
    props: {},
    events: []
  },

  [ControlType.DateTime]: {
    attributeKey: 'dateTime',
    props: {useTime: true},
    events: [COMPONENT_ACTIONS.onChange]
  },
  
  [ControlType.Date]: {
    attributeKey: 'date',
    props: {useTime: false},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.TextBox]: {
    attributeKey: 'textBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.NumberBox]: {
    attributeKey: 'numberBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.SelectMultiple]: {
    attributeKey: 'selectMultiple',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onLoadCategoryOptions]
  },

  [ControlType.SelectMultipleHierarchy]: {
    attributeKey: 'selectMultipleHierarchy',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onLoadCategoryOptions]
  },

  [ControlType.SelectSingle]: {
    attributeKey: 'selectSingle',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onLoadCategoryOptions]
  },

  [ControlType.SelectSingleHierarchy]: {
    attributeKey: 'selectSingleHierarchy',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onLoadCategoryOptions]
  },

  [ControlType.TextArea]: {
    attributeKey: 'textArea',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.TileView]: {
    attributeKey: 'tileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.SelectSingleShortList]: {
    attributeKey: 'selectSingleShortList',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.EntityKeyCategorySelectSingleShortList]: {
    attributeKey: 'selectSingleShortList',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.CommentTile]: {
    attributeKey: 'commentTile',
    props: {},
    events: [COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.CommentTileView]: {
    attributeKey: 'commentTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.SliderRating]: {
    attributeKey: 'sliderRating',
    props: { scheduleUpdate },
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.EntityKeyCategorySliderRating]: {
    attributeKey: 'sliderRating',
    props: { scheduleUpdate },
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.ButtonList]: {
    attributeKey: 'buttonList',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.HeaderPanel]: {
    attributeKey: 'headerPanel',
    props: {},
    events: [COMPONENT_ACTIONS.onEditDocument, COMPONENT_ACTIONS.onDeleteDocument, COMPONENT_ACTIONS.onDuplicateDocument,
      COMPONENT_ACTIONS.onLoadBowtie, COMPONENT_ACTIONS.onSubmitDocument, COMPONENT_ACTIONS.onCloseDocument]
  },

  [ControlType.DisplayCodes]: {
    attributeKey: 'displayCodes',
    props: {},
    events: []
  },

  [ControlType.DisplayCodesUnitOfMeasure]: {
    attributeKey: 'displayCodesUnitOfMeasure',
    props: {},
    events: []
  },

  [ControlType.DisplayDate]: {
    attributeKey: 'displayDate',
    props: {},
    events: []
  },

  [ControlType.DisplayNumber]: {
    attributeKey: 'displayNumber',
    props: {},
    events: []
  },

  [ControlType.DisplayNumberUnitOfMeasure]: {
    attributeKey: 'displayNumberUnitOfMeasure',
    props: {},
    events: []
  },

  [ControlType.DisplayText]: {
    attributeKey: 'displayText',
    props: {},
    events: []
  },

  [ControlType.LinkHeading]: {
    attributeKey: 'primaryTitle',
    props: {},
    events: []
  },

  [ControlType.FluidPanel]: {
    attributeKey: 'fluidPanel',
    props: {},
    events: []
  },

  [ControlType.ExpandableText]: {
    attributeKey: 'expandableText',
    props: {},
    events: []
  },

  [ControlType.ProcessPanel]: {
    attributeKey: 'processPanel',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.GroupPanel]: {
    attributeKey: 'groupPanel',
    props: {},
    events: []
  },

  [ControlType.FlexPanel]: {
    attributeKey: 'flexPanel',
    props: {},
    events: []
  },

  [ControlType.ColumnsPanel]: {
    attributeKey: 'columnsPanel',
    props: {},
    events: []
  },

  [ControlType.DisplayChildValues]: {
    attributeKey: 'displayCommaSeparatedValues',
    props: {},
    events: []
  },

  [ControlType.FactBox]: {
    attributeKey: 'factBox',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesTileView]: {
    attributeKey: 'connectedCasesTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem, COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.FileUploader]: {
    attributeKey: 'fileUploader',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.CodeSelectorTileView]: {
    attributeKey: 'codeSelectorTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.LossPotentialEvaluation]: {
    attributeKey: 'lossPotentialEvaluation',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem, COMPONENT_ACTIONS.onCopyDocumentItem]
  },

  [ControlType.LossPotentialEvaluationTileView]: {
    attributeKey: 'lossPotentialEvaluationTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem, COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.CodeTitleTile]: {
    attributeKey: 'codeTitleTile',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.LossPotentialConsequenceEvaluation]: {
    attributeKey: 'lossPotentialConsequenceEvaluation',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.LossPotentialConsequenceEvaluationTileView]: {
    attributeKey: 'lossPotentialConsequenceEvaluationTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.CategoryTileView]: {
    attributeKey: 'categoryTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.CategoryTile]: {
    attributeKey: 'categoryTile',
    props: {},
    events: []
  },

  [ControlType.DetailForm]: {
    attributeKey: 'detailForm',
    props: {},
    events: []
  },

  [ControlType.DisplayNumericHeader]: {
    attributeKey: 'displayNumericHeader',
    props: {},
    events: []
  },

  [ControlType.Tile]: {
    attributeKey: 'tile',
    props: {},
    events: [
      COMPONENT_ACTIONS.onSelectDocumentItem,
      COMPONENT_ACTIONS.onEditDocumentItem,
      COMPONENT_ACTIONS.onDeleteDocumentItem,
      COMPONENT_ACTIONS.onCopyDocumentItem
    ]
  },

  [ControlType.DisplayCodesHierarchy]: {
    attributeKey: 'displayCodesHierarchy',
    props: {},
    events: []
  },

  [ControlType.DisplayBoolean]: {
    attributeKey: 'displayBoolean',
    props: {},
    events: []
  },

  [ControlType.ConsequenceTypeTileView]: {
    attributeKey: 'consequenceTypeTileView',
    props: { rowClassName: 'col-12' },
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.ConsequenceTypeTile]: {
    attributeKey: 'consequenceTypeTile',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.ConsequenceTileView]: {
    attributeKey: 'consequenceTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem, COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.CheckBox]: {
    attributeKey: 'checkBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.LongDescription]: {
    attributeKey: 'longDescription',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesTile]: {
    attributeKey: 'tile',
    props: {},
    events: [COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.CauseCategoryTileView]: {
    attributeKey: 'categoryTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.CheckListCategoryTileView]: {
    attributeKey: 'categoryTileView',
    props: { rowClassName: 'col-12' },
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.CheckListCategoryTile]: {
    attributeKey: 'categoryTile',
    props: {},
    events: [COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.SectionSummaryPanel]: {
    attributeKey: 'sectionSummary',
    props: {},
    events: []
  },

  [ControlType.TileTopZone]: {
    attributeKey: 'tileTopZone',
    props: {},
    events: []
  },

  [ControlType.DisplayNumericSum]: {
    attributeKey: 'displayNumericSum',
    props: {
      strong: true
    },
    events: []
  },

  [ControlType.AttachLinkTileView]: {
    attributeKey: 'attachLinkTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.DisplaySafeAnchor]: {
    attributeKey: 'displaySafeAnchor',
    props: {},
    events: []
  },

  [ControlType.AttachLinkTile]: {
    attributeKey: 'attachLinkTile',
    props: {
      enabledChildEntityOperations: { remove: false }
    },
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.CheckListEvaluationPanel]: {
    attributeKey: 'checkListEvaluationPanel',
    props: {},
    events: []
  },

  [ControlType.SubstanceCategoryTileView]: {
    attributeKey: 'categoryTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.EnvironmentalValuesTileView]: {
    attributeKey: 'codeSelectorTileView',
    props: { codeDataTag: columnName[tableName.green].envValSubstance },
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.SafeAnchor]: {
    attributeKey: 'safeAnchor',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.InteractionTileView]: {
    attributeKey: 'categoryTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.InteractionTile]: {
    attributeKey: 'codeTitleTile',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.CausesTileView]: {
    attributeKey: 'codeSelectorTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.CompactCodeTitleTile]: {
    attributeKey: 'codeTitleTile',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.SummaryTileView]: {
    attributeKey: 'summaryTileView',
    props: {},
    events: []
  },

  [ControlType.TitleWrapperPrimaryKey]: {
    attributeKey: 'titleWrapper',
    props: {},
    events: []
  },

  [ControlType.InteractionTileViewDrugReaction]: {
    attributeKey: 'interactionTileViewDrugReaction',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.ReferenceTileView]: {
    attributeKey: 'codeSelectorTileView',
    props: {},
    events: [COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.ReferenceTile]: {
    attributeKey: 'codeTitleTile',
    props: {},
    events: [COMPONENT_ACTIONS.onSelectDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.ReferenceDate]: {
    attributeKey: 'dateTime',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.ReferenceTextBox]: {
    attributeKey: 'textBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.ReferenceNumberBox]: {
    attributeKey: 'numberBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.ReferenceEntityKeyCategorySelectSingleShortList]: {
    attributeKey: 'selectSingleShortList',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.ReferenceCheckBox]: {
    attributeKey: 'checkBox',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.ReferenceDateTime]: {
    attributeKey: 'dateTime',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.ReferenceSelectSingleHierarchy]: {
    attributeKey: 'selectSingleHierarchy',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.ReferenceSelectSingle]: {
    attributeKey: 'selectSingle',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.SurveyLink]: {
    attributeKey: 'link',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.Signature]: {
    attributeKey: 'signature',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.SectionPanel]: {
    attributeKey: 'sectionPanel',
    props: {
      // opt out from null rendering since hide criterias messes up tab ordering
      noConditionalRender: true
    },
    events: [COMPONENT_ACTIONS.onSelectDocumentItem]
  },

  [ControlType.RiskMatrix]: {
    attributeKey: 'riskMatrix',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.DisplayInsuranceBalance]: {
    attributeKey: 'displayInsuranceBalance',
    props: {},
    events: []
  },

  [ControlType.IndicatorCodeSelectorTileView]: {
    attributeKey: 'indicatorCodeSelectorTileView',
    props: { rowClassName: 'col-md-6 col-xl-4' },
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.AddDocumentItemLinkButton]: {
    attributeKey: 'addDocumentItemLinkButton',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.ProcessingLogView]: {
    attributeKey: 'processingLogView',
    props: {},
    events: []
  },

  [ControlType.ProcessingLogDetails]: {
    attributeKey: 'processingLogDetails',
    props: {},
    events: []
  },

  [ControlType.ProcessingLogLink]: {
    attributeKey: 'processingLogLink',
    props: {},
    events: []
  },

  [ControlType.CaseLogLink]: {
    attributeKey: 'caseLogLink',
    props: {},
    events: []
  },

  [ControlType.MailLogDetails]: {
    attributeKey: 'mailLogDetails',
    props: {},
    events: []
  },

  [ControlType.MailLogLink]: {
    attributeKey: 'mailLogLink',
    props: {},
    events: []
  },

  [ControlType.AddNewCaseButton]: {
    attributeKey: 'addNewCaseButton',
    props: {},
    events: []
  },

  [ControlType.CompactTileHeader]: {
    attributeKey: 'compactTileHeader',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesFactbox]: {
    attributeKey: 'connectedCasesFactbox',
    props: {},
    events: []
  },

  [ControlType.Link]: {
    attributeKey: 'link',
    props: {},
    events: []
  },

  [ControlType.CheckForDuplicateRegistrationsButton]: {
    attributeKey: 'checkForDuplicateRegistrationsButton',
    props: {},
    events: [COMPONENT_ACTIONS.onAddDocumentItem, COMPONENT_ACTIONS.onDeleteDocumentItem]
  },

  [ControlType.DisplaySurveyLink]: {
    attributeKey: 'displaySurveyLink',
    props: {},
    events: []
  },

  [ControlType.LinkSearchExternalProvider]: {
    attributeKey: 'linkSearchExternalProvider',
    props: {},
    events: []
  },

  [ControlType.CompactTile]: {
    attributeKey: 'compactTile',
    props: {},
    events: [
      COMPONENT_ACTIONS.onSelectDocumentItem,
      COMPONENT_ACTIONS.onEditDocumentItem,
      COMPONENT_ACTIONS.onDeleteDocumentItem,
      COMPONENT_ACTIONS.onCopyDocumentItem,
      COMPONENT_ACTIONS.onSubmitDocument
    ]
  },

  [ControlType.ConnectToBowtieButton]: {
    attributeKey: 'connectToBowtieButton',
    props: {},
    events: []
  },

  [ControlType.BarrierInspectionTileView]: {
    attributeKey: 'barrierInspectionTileView',
    props: { rowClassName: 'col-12 col-sm-6 col-lg-4' },
    events: []
  },

  [ControlType.DisplayNumberBarrierInspection]: {
    attributeKey: 'displayNumberBarrierInspection',
    props: {},
    events: []
  },
  
  [ControlType.DisplayCodesStatus]: {
    attributeKey: 'displayCodesStatus',
    props: {},
    events: []
  },

  [ControlType.SelectSingleSignature]: {
    attributeKey: 'selectSingleSignature',
    props: {},
    events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions]
  },

  [ControlType.DisplayEnvironmentalPreviousValues]: {
    attributeKey: 'displayEnvironmentalPreviousValues',
    props: {},
    events: []
  },

  [ControlType.ShareLinkButton]: {
    attributeKey: 'shareLinkButton',
    props: {},
    events: []
  },

  [ControlType.MultiSelectRecipients]: {
    attributeKey: 'multiSelectRecipients',
    props: {},
    events: [COMPONENT_ACTIONS.onChange]
  },

  [ControlType.BadgeListFileUploader]: {
    attributeKey: 'badgeListFileUploader',
    props: {},
    events: []
  },

  [ControlType.Thumbnail]: {
    attributeKey: 'thumbnail',
    props: {},
    events: []
  },

  [ControlType.DetailsPanelHeader]: {
    attributeKey: 'detailsPanelHeader',
    props: {},
    events: []
  },

  [ControlType.LinkButtonFileUploader]: {
    attributeKey: 'linkButtonFileUploader',
    props: {},
    events: []
  },

  [ControlType.FileUploaderList]: {
    attributeKey: 'fileUploaderList',
    props: {},
    events: []
  },

  [ControlType.ChangeLogLink]: {
    attributeKey: 'changeLogLink',
    props: {},
    events: []
  },

  [ControlType.LossPotentialEvaluationFooter]: {
    attributeKey: 'lossPotentialEvaluationFooter',
    props: {},
    events: []
  },

  [ControlType.LossPotentialConsequenceEvaluationFooter]: {
    attributeKey: 'lossPotentialConsequenceEvaluationFooter',
    props: {},
    events: []
  },

  [ControlType.LossPotentialConsequenceEvaluationTileRiskMatrixPanel]: {
    attributeKey: 'lossPotentialConsequenceEvaluationTileRiskMatrixPanel',
    props: {},
    events: []
  },
  
  [ControlType.LossPotentialConsequenceEvaluationTileDetailPanel]: {
    attributeKey: 'lossPotentialConsequenceEvaluationTileDetailPanel',
    props: {},
    events: []
  },
  [ControlType.ButtonGroupPanel]: {
    attributeKey: 'buttonGroupPanel',
    props: {},
    events: []
  },

  [ControlType.AssessmentFactItem]: {
    attributeKey: 'assessmentFactItem',
    props: {},
    events: []
  },

  [ControlType.BowtieFactItem]: {
    attributeKey: 'bowtieFactItem',
    props: {},
    events: []
  },

  [ControlType.CausesFactItem]: {
    attributeKey: 'causesFactItem',
    props: {},
    events: []
  },

  [ControlType.ChecklistFactItem]: {
    attributeKey: 'checklistFactItem',
    props: {},
    events: []
  },

  [ControlType.CommentsFactItem]: {
    attributeKey: 'commentsFactItem',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesFactItem]: {
    attributeKey: 'connectedCasesFactItem',
    props: {},
    events: []
  },

  [ControlType.ConsequencesFactItem]: {
    attributeKey: 'consequencesFactItem',
    props: {},
    events: []
  },

  [ControlType.IntegrityStatusFactItem]: {
    attributeKey: 'integrityStatusFactItem',
    props: {},
    events: []
  },

  [ControlType.InvolvedDrugFactItem]: {
    attributeKey: 'involvedDrugFactItem',
    props: {},
    events: []
  },

  [ControlType.InvolvedPersonFactItem]: {
    attributeKey: 'involvedPersonFactItem',
    props: {},
    events: []
  },

  [ControlType.ResidualRiskFactItem]: {
    attributeKey: 'residualRiskFactItem',
    props: {},
    events: []
  },

  [ControlType.RiskFactItem]: {
    attributeKey: 'riskFactItem',
    props: {},
    events: []
  },

  [ControlType.ActionFactItem]: {
    attributeKey: 'actionFactItem',
    props: {},
    events: []
  },

  [ControlType.AttachedFilesFactItem]: {
    attributeKey: 'attachedFilesFactItem',
    props: {},
    events: []
  },

  [ControlType.IntegrityStatusBarrierEvalFactItem]: {
    attributeKey: 'integrityStatusBarrierEvalFactItem',
    props: {},
    events: []
  },
  
  [ControlType.FactItemPanel]: {
    attributeKey: 'factItemPanel',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesRiskFactItem]: {
    attributeKey: 'connectedCasesRiskFactItem',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesResidualRiskFactItem]: {
    attributeKey: 'connectedCasesResidualRiskFactItem',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesActionFactItem]: {
    attributeKey: 'connectedCasesActionFactItem',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesIntegrityStatusFactItem]: {
    attributeKey: 'connectedCasesIntegrityStatusFactItem',
    props: {},
    events: []
  },

  [ControlType.ConnectedCasesIntegrityStatusBarrierEvalFactItem]: {
    attributeKey: 'connectedCasesIntegrityStatusBarrierEvalFactItem',
    props: {},
    events: []
  },
  
  [ControlType.ConnectedCasesBowtieFactItem]: {
    attributeKey: 'connectedCasesBowtieFactItem',
    props: {},
    events: []
  },

  [ControlType.ImagesFactItem]: {
    attributeKey: 'imagesFactItem',
    props: {},
    events: []
  },

  [ControlType.DisplayLink]: {
    attributeKey: 'displayLink',
    props: {},
    events: []
  },

  [ControlType.ReportCategoryFactItem]: {
    attributeKey: 'reportCategoryFactItem',
    props: {},
    events: []
  },

   [ControlType.ConnectedCasesReportCategoryFactItem]: {
    attributeKey: 'connectedCasesReportCategoryFactItem',
    props: {},
    events: []
  },

  [ControlType.GasMeasurementCodeSelectorTileView]: {
  attributeKey: 'gasMeasurementCodeSelectorTileView',
  props: { rowClassName: 'col-md-6' },
  events: [COMPONENT_ACTIONS.onChange, COMPONENT_ACTIONS.onLoadOptions, COMPONENT_ACTIONS.onAddDocumentItem]
  },

  [ControlType.GasTestFactItem]: {
    attributeKey: 'gasTestFactItem',
    props: {},
    events: []
  },

  [ControlType.CompactTileLinkHeading]: {
    attributeKey: 'compactTileLinkHeading',
    props: {},
    events: [
      COMPONENT_ACTIONS.onSelectDocumentItem      
    ]
  }
};
